
import React, { createContext, useContext, ReactNode } from 'react';
import useCostCalculator, { 
  EstimationOptions, 
  CostBreakdown, 
  Platform, 
  AppType, 
  Feature, 
  UserVolume, 
  Maintenance 
} from '@/hooks/useCostCalculator';
import { BaseUrlContext } from '@/components/web-components/utils/renderer/contexts';

interface CostEstimatorContextType {
  options: EstimationOptions;
  costs: CostBreakdown;
  updateOptions: (newOptions: Partial<EstimationOptions>) => void;
  togglePlatform: (platform: Platform) => void;
  setAppType: (appType: AppType) => void;
  toggleFeature: (feature: Feature) => void;
  setUserVolume: (userVolume: UserVolume) => void;
  setMaintenance: (maintenance: Maintenance | undefined) => void;
  COSTS: any;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  totalSteps: number;
  baseUrl: string; // Add baseUrl to context
}

const CostEstimatorContext = createContext<CostEstimatorContextType | undefined>(undefined);

export const useCostEstimator = () => {
  const context = useContext(CostEstimatorContext);
  if (!context) {
    throw new Error('useCostEstimator must be used within a CostEstimatorProvider');
  }
  return context;
};

interface CostEstimatorProviderProps {
  children: ReactNode;
  initialOptions?: Partial<EstimationOptions>;
  initialStep?: number;
  totalSteps?: number;
}

export const CostEstimatorProvider = ({
  children,
  initialOptions,
  initialStep = 1,
  totalSteps = 5
}: CostEstimatorProviderProps) => {
  const calculator = useCostCalculator(initialOptions);
  const [currentStep, setCurrentStep] = React.useState(initialStep);
  const baseUrlCtx = useContext(BaseUrlContext);
  // Ensure baseUrl is a string to match the type in CostEstimatorContextType
  const baseUrl = baseUrlCtx || '';

  const goToNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prevStep => prevStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prevStep => prevStep - 1);
    }
  };

  const value: CostEstimatorContextType = {
    ...calculator,
    currentStep,
    setCurrentStep,
    goToNextStep,
    goToPreviousStep,
    totalSteps,
    baseUrl // Add baseUrl to the value
  };

  return (
    <CostEstimatorContext.Provider value={value}>
      {children}
    </CostEstimatorContext.Provider>
  );
};
